import { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useCommunityFeed, PlatformFilter } from "@/hooks/useCommunityFeed";
import { StoryCard } from "@/components/community/StoryCard";
import { StorySubmissionForm } from "@/components/community/StorySubmissionForm";
import { PlatformBadge } from "@/components/community/PlatformBadge";
import { MessageSquarePlus, X, ChevronDown, Users, Hash } from "lucide-react";

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const T_LIGHT = {
  bg: "var(--civic-cream)",
  cardBg: "#FFFFFF",
  text: "var(--civic-ink)",
  textMuted: "rgba(var(--civic-ink-rgb),0.62)",
  red: "var(--civic-red)",
  blue: "var(--civic-navy)",
  gold: "var(--civic-gold)",
  green: "var(--civic-money)",
  border: "rgba(var(--civic-ink-rgb),0.10)",
  modalBg: "#FFFFFF",
  skeletonBg: "#e5e7eb",
  skeletonBgLight: "#f3f4f6",
};
const T_DARK = {
  bg: "var(--civic-bg-dark)",
  cardBg: "var(--civic-navy-panel)",
  text: "var(--civic-cream)",
  textMuted: "rgba(var(--civic-cream-rgb),0.55)",
  red: "var(--civic-red)",
  blue: "#5B9BD5",
  gold: "var(--civic-gold)",
  green: "var(--civic-money)",
  border: "rgba(var(--civic-cream-rgb),0.10)",
  modalBg: "var(--civic-navy-panel)",
  skeletonBg: "rgba(var(--civic-cream-rgb),0.08)",
  skeletonBgLight: "rgba(var(--civic-cream-rgb),0.04)",
};

const PLATFORM_FILTERS: { value: PlatformFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'twitter', label: 'X' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'direct', label: 'Direct Stories' },
];

const CommunityStories = () => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const T = isDark ? T_DARK : T_LIGHT;
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCommunityFeed(platformFilter);

  const allItems = data?.pages.flat() || [];

  // Separate pinned/featured from regular items
  const pinnedItems = allItems.filter(i => i.is_pinned);
  const featuredItems = allItems.filter(i => i.is_featured && !i.is_pinned);
  const regularItems = allItems.filter(i => !i.is_pinned && !i.is_featured);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <>
      <Helmet>
        <title>Patient Stories — Real Healthcare Cost Experiences | {brand.name}</title>
        <meta name="description" content="Real stories from Americans dealing with hospital bills and healthcare prices. Share your experience and help others make informed healthcare decisions." />
        <meta name="keywords" content="patient stories, hospital bill stories, healthcare cost experiences, medical debt stories" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${brand.primaryDomain}/stories`} />
        <meta property="og:title" content="Patient Stories — Real Healthcare Cost Experiences" />
        <meta property="og:description" content="Real stories from Americans dealing with hospital bills and healthcare prices. Share your experience and help others make informed healthcare decisions." />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${brand.primaryDomain}/stories`} />
        <meta property="og:site_name" content={brand.name} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Patient Stories — Real Healthcare Cost Experiences" />
        <meta name="twitter:description" content="Real stories from Americans dealing with hospital bills and healthcare prices. Share your experience and help others make informed healthcare decisions." />
        <meta name="twitter:image" content={brand.social.ogImage} />
        {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
      </Helmet>

      <div style={{ background: T.bg, minHeight: '100vh', fontFamily: sans, paddingBottom: '100px' }}>
        {/* Hero section */}
        <section
          className="relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${T.blue} 0%, var(--civic-navy-deep) 100%)` }}
        >
          <div className="max-w-4xl mx-auto px-4 py-16 md:py-20 text-center relative z-10">
            {/* Decorative stars */}
            <div className="flex justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <span key={i} style={{ color: T.gold, fontSize: '18px' }}>&#9733;</span>
              ))}
            </div>

            <h1
              className="text-3xl md:text-5xl font-bold mb-4 text-white"
              style={{ fontFamily: serif }}
            >
              Community Stories
            </h1>
            <p className="text-lg md:text-xl mb-6 max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.80)' }}>
              Real Americans sharing their hospital pricing experiences. Your story could help millions.
            </p>

            {/* CTA: how to participate */}
            <div
              className="inline-block rounded-xl px-6 py-4 mb-6"
              style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Hash className="w-5 h-5" style={{ color: T.gold }} />
                <span className="font-bold text-white text-lg" style={{ fontFamily: serif }}>
                  #AmericanHealthPricesStory
                </span>
              </div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.70)' }}>
                Post on X, Instagram, TikTok, Facebook, or YouTube with this hashtag — or share directly below.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setShowSubmitForm(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-transform hover:scale-105"
                style={{ background: T.red, color: '#FFFFFF' }}
              >
                <MessageSquarePlus className="w-5 h-5" />
                Share Your Story
              </button>
              <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
                <Users className="w-4 h-4" />
                {allItems.length > 0 ? `${allItems.length}+ stories shared` : 'Be the first to share'}
              </div>
            </div>
          </div>

          {/* Subtle flag stripes decoration */}
          <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
            <div className="flex-1" style={{ background: T.red }} />
            <div className="flex-1" style={{ background: '#FFFFFF' }} />
            <div className="flex-1" style={{ background: T.red }} />
          </div>
        </section>

        {/* Submit form modal */}
        {showSubmitForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div
              className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl p-6 shadow-2xl"
              style={{ background: T.modalBg }}
            >
              <button
                onClick={() => setShowSubmitForm(false)}
                className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5" style={{ color: T.text }} />
              </button>
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: serif, color: T.text }}>
                Share Your Story
              </h2>
              <p className="text-sm mb-6" style={{ color: T.textMuted }}>
                Your experience matters. Help others understand hospital pricing.
              </p>
              <StorySubmissionForm onSuccess={() => setShowSubmitForm(false)} />
            </div>
          </div>
        )}

        {/* Platform filter bar */}
        <div className="sticky top-0 z-30" style={{ background: T.bg, borderBottom: `1px solid ${T.border}` }}>
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
              {PLATFORM_FILTERS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setPlatformFilter(value)}
                  className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all"
                  style={{
                    background: platformFilter === value ? T.blue : 'transparent',
                    color: platformFilter === value ? '#FFFFFF' : T.textMuted,
                    border: `1px solid ${platformFilter === value ? T.blue : T.border}`,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Feed */}
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
          {/* Pinned posts */}
          {pinnedItems.map(item => (
            <StoryCard key={item.id} item={item} isDark={isDark} />
          ))}

          {/* Featured posts */}
          {featuredItems.map(item => (
            <StoryCard key={item.id} item={item} isDark={isDark} />
          ))}

          {/* Regular posts */}
          {regularItems.map(item => (
            <StoryCard key={item.id} item={item} isDark={isDark} />
          ))}

          {/* Loading state */}
          {isLoading && (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl p-5 animate-pulse"
                  style={{ background: T.cardBg, border: `1px solid ${T.border}` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full" style={{ background: T.skeletonBg }} />
                    <div className="flex-1">
                      <div className="h-4 w-32 rounded mb-1" style={{ background: T.skeletonBg }} />
                      <div className="h-3 w-20 rounded" style={{ background: T.skeletonBgLight }} />
                    </div>
                  </div>
                  <div className="h-4 w-full rounded mb-2" style={{ background: T.skeletonBgLight }} />
                  <div className="h-4 w-3/4 rounded" style={{ background: T.skeletonBgLight }} />
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && allItems.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">&#x1F3F3;&#xFE0F;</div>
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: serif, color: T.text }}>
                No Stories Yet
              </h3>
              <p className="text-sm mb-4" style={{ color: T.textMuted }}>
                Be the first to share your hospital pricing experience.
              </p>
              <button
                onClick={() => setShowSubmitForm(true)}
                className="px-6 py-2.5 rounded-lg font-semibold text-sm text-white"
                style={{ background: T.red }}
              >
                Share Your Story
              </button>
            </div>
          )}

          {/* Infinite scroll trigger */}
          <div ref={loadMoreRef} className="h-4" />

          {isFetchingNextPage && (
            <div className="text-center py-4">
              <div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-civic-red rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CommunityStories;
